import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEditorStore } from "../../../lib/stores/editor-store/editor-store";
import { ImageListItem, SortableImageListItem } from "./ImageListItem";
import { useEffect, useRef, useState } from "react";
import { ImageUploadButton } from "../ImageUpload";
import { useDropzone } from "react-dropzone";
import ImagesBottomBar from "./ImagesBottomBar";
import Toast from "@/lib/components/Toast";
import TabTitle from "../TabTitle";
import OpenScraperButton from "./OpenScraperButton";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";

type UploadProgress = {
  type: "sourceImages" | "animatedImages" | "both";
  completed: number;
  total: number;
};

export default function ImagesTab() {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null,
  );
  const isUploading = uploadProgress !== null;

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );
  const timelineItems =
    useEditorStore((state) => state.video?.compositionJson.mainSceneTimeline) ??
    [];
  const handleImageDrag = useEditorStore(
    (state) => state.service.handleImageDrag,
  );
  const seekToImage = useEditorStore((state) => state.service.seekToImage);

  const handleImageDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
  };

  const handleImageDragEnd = (event: DragEndEvent) => {
    handleImageDrag(event);
    setActiveId(null);
    seekToImage(event.active.id as string);
  };

  const uploadImages = useEditorStore(
    (state) => state.service.uploadSourceImagesSyncComposition,
  );
  const uploadAnimatedImages = useEditorStore(
    (state) => state.service.uploadAnimatedImagesSyncComposition,
  );

  const setIsImageSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsImageSelectorDialogOpen,
  );

  const anyDialogOpen = useEditorStore(
    (state) =>
      state.sourceImageSelectorDialogState.isOpen ||
      state.quickAnimationsDialogState.openTimelineItemExternalId !== null,
  );

  const { getRootProps, getInputProps, open, isDragActive, rootRef } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      disabled: anyDialogOpen,
      accept: {
        "image/png": [".png"],
        "image/jpeg": [".jpg", ".jpeg"],
        "image/webp": [".webp"],
        "video/mp4": [".mp4"],
        "video/quicktime": [".mov"],
        "video/webm": [".webm"],
      },
      onDrop: async (acceptedFiles) => {
        if (isUploading) {
          return;
        }

        const byName = (a: File, b: File) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          });

        const images = acceptedFiles
          .filter((file) => file.type.startsWith("image/"))
          .sort(byName);
        const videos = acceptedFiles
          .filter((file) => file.type.startsWith("video/"))
          .sort(byName);

        const totalCount = images.length + videos.length;
        if (totalCount === 0) return;

        let uploadType: "sourceImages" | "animatedImages" | "both";

        if (images.length > 0 && videos.length > 0) {
          uploadType = "both";
        } else if (images.length > 0) {
          uploadType = "sourceImages";
        } else {
          uploadType = "animatedImages";
        }

        setUploadProgress({
          type: uploadType,
          completed: 0,
          total: totalCount,
        });

        let imagesCompleted = 0;
        let videosCompleted = 0;

        try {
          if (images.length > 0) {
            await uploadImages(images, (completed) => {
              imagesCompleted = completed;
              setUploadProgress({
                type: uploadType,
                completed: imagesCompleted + videosCompleted,
                total: totalCount,
              });
            });
          }

          if (videos.length > 0) {
            await uploadAnimatedImages(videos, (completed) => {
              videosCompleted = completed;
              setUploadProgress({
                type: uploadType,
                completed: imagesCompleted + videosCompleted,
                total: totalCount,
              });
            });
          }
        } catch (err) {
          console.error(err);
          Toast.error("Failed to upload files");
        } finally {
          setUploadProgress(null);
        }
      },
    });

  const previousItemCountRef = useRef(timelineItems.length);
  useEffect(() => {
    const previousCount = previousItemCountRef.current;
    const currentCount = timelineItems.length;

    if (previousCount > 0 && currentCount > previousCount) {
      const el = rootRef.current;
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    }

    previousItemCountRef.current = currentCount;
  }, [timelineItems.length, rootRef]);

  const isImagesEmpty = timelineItems.length === 0;

  return (
    <div className="relative flex h-full w-full flex-col">
      <TabTitle
        title="Images"
        subtitle="Start by uploading images."
        isVisible={isImagesEmpty}
      />
      <div
        className={cn(
          "flex flex-1 flex-col overflow-y-auto pr-6",
          isImagesEmpty ? "pb-6" : "gap-3 py-6",
        )}
        {...getRootProps()}
      >
        {!isDragActive && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleImageDragStart}
            onDragEnd={handleImageDragEnd}
          >
            <SortableContext
              items={timelineItems.map((item) => item.externalId)}
              strategy={verticalListSortingStrategy}
            >
              {timelineItems.map((item, index) => (
                <SortableImageListItem
                  key={item.externalId}
                  index={index}
                  timelineItem={item}
                  totalTimelineItems={timelineItems.length}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <ImageListItem
                  key={activeId}
                  isDragOverlay
                  isDraggingAny
                  timelineItem={
                    timelineItems.find((item) => item.externalId === activeId)!
                  }
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
        <ImageUploadButton
          getInputProps={getInputProps}
          openImageUpload={open}
          openImageSelectorDialog={() => setIsImageSelectorDialogOpen(true)}
          isDragActive={isDragActive}
          isImagesEmpty={timelineItems.length === 0}
          className={isImagesEmpty ? "ml-6" : "ml-8"}
        />
        <OpenScraperButton isVisible={isImagesEmpty} />
      </div>
      <ImagesBottomBar isVisible={timelineItems.length !== 0} />
      {isUploading && <UploadingIndicator progress={uploadProgress} />}
    </div>
  );
}

const typeLabels = {
  sourceImages: "images",
  animatedImages: "videos",
  both: "files",
} as const;

function UploadingIndicator({ progress }: { progress: UploadProgress }) {
  const typeLabel = typeLabels[progress.type];

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/95">
      <LoadingSpinner stroke={1.5} size={24} color="url(#icon-gradient)" />
      <div className="h-3" />
      <h1 className="text-base font-semibold">Uploading {typeLabel}</h1>
      <p className="text-sm text-text-secondary">
        {progress.completed}/{progress.total} {typeLabel} uploaded
      </p>
    </div>
  );
}
